import React, {useEffect, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import pushService from "../../_services/cart/push.service";
import authService from "../../_services/cart/authService";

/**
 * El cartel de la home que le ofrece las notificaciones al usuario logueado.
 *
 * <h3>Por qué en la home y no sólo en la campana</h3>
 * El opt-in que vive dentro del panel de la campana sólo lo encuentra quien ya fue a mirar sus
 * avisos, que es justo el que menos los necesita. El que hay que alcanzar es el vendedor que entra,
 * mira publicaciones y se va sin enterarse de que le vendieron algo. Por eso el pedido aparece acá,
 * una vez que hay sesión: sin sesión no hay a quién asociarle el token y el permiso se gastaría al
 * pedo.
 *
 * <h3>Por qué no se pide solo al cargar</h3>
 * Un `requestPermission()` disparado sin que la persona lo haya buscado es la forma más rápida de
 * que lo bloquee, y el bloqueo NO se puede revertir desde el código: no hay segunda oportunidad en
 * ese navegador nunca más. Así que lo que aparece solo es el cartel —que explica para qué sirve— y
 * el permiso lo pide el click del botón. Ese click además es requisito técnico en Safari y en los
 * Chrome de Android, que descartan el pedido si no viene de un gesto del usuario.
 */

/** Cuánto se calla el cartel después de un "ahora no", y cuántos "ahora no" aguanta antes de rendirse. */
const DIAS_DE_ESPERA = 7;
const MAX_POSPUESTOS = 3;
const CLAVE = "pushOptIn";

/** Un respiro antes de aparecer: encimado al render inicial es un cartel que nadie lee. */
const DEMORA_MS = 2500;

const leerEstado = () => {
    try {
        return JSON.parse(localStorage.getItem(CLAVE)) || {};
    } catch (e) {
        // Modo privado o storage lleno: que no se muestre nunca es peor que mostrarlo de más.
        return {};
    }
};

const guardarEstado = (estado) => {
    try {
        localStorage.setItem(CLAVE, JSON.stringify(estado));
    } catch (e) {
        /* sin storage el cartel vuelve a aparecer en la próxima visita, y está bien */
    }
};

export const PushOptIn = () => {

    const [visible, setVisible] = useState(false);
    const [estado, setEstado] = useState("oferta"); // oferta | ios | bloqueado | error | listo

    useEffect(() => {
        if (!authService.isAuthenticated()) return undefined;

        // iOS fuera de la app instalada no puede, pero sí puede instalarla: ahí el cartel cambia de
        // pedido de permiso a instructivo, porque el botón no haría nada.
        const modo = pushService.iosSinInstalar
            ? "ios"
            : pushService.sePuedePreguntar
                ? "oferta"
                : null;

        if (!modo) return undefined;

        const {hasta = 0, veces = 0} = leerEstado();
        if (veces >= MAX_POSPUESTOS || Date.now() < hasta) return undefined;

        setEstado(modo);
        const t = setTimeout(() => setVisible(true), DEMORA_MS);
        return () => clearTimeout(t);
    }, []);

    const posponer = () => {
        const {veces = 0} = leerEstado();
        guardarEstado({veces: veces + 1, hasta: Date.now() + DIAS_DE_ESPERA * 24 * 60 * 60 * 1000});
        setVisible(false);
    };

    const activar = async () => {
        const {success, blocked, error} = await pushService.activar();

        if (success) {
            setEstado("listo");
            // Queda un momento en pantalla confirmando; irse de golpe parece que no pasó nada.
            setTimeout(() => setVisible(false), 2500);
            guardarEstado({veces: MAX_POSPUESTOS, hasta: 0});
            return;
        }

        if (blocked) {
            setEstado("bloqueado");
            return;
        }

        setEstado(error ? "error" : "oferta");
        if (!error) setVisible(false);
    };

    if (!visible) return null;

    return (
        <aside className="pushOptIn" role="region" aria-label="Notificaciones">
            <span className="pushOptIn__icono">
                <FontAwesomeIcon icon="fa-solid fa-bell"/>
            </span>

            <div className="pushOptIn__texto">
                {estado === "oferta" && (
                    <>
                        <strong>Activá las notificaciones</strong>
                        <p>
                            Te avisamos al instante cuando te compran, te pagan o te escriben, aunque no
                            tengas DeAutopartes abierto.
                        </p>
                    </>
                )}

                {estado === "ios" && (
                    <>
                        <strong>Recibí avisos en tu iPhone</strong>
                        <p>
                            Tocá <FontAwesomeIcon icon="fa-solid fa-arrow-up-from-bracket"/> Compartir y
                            después <em>Agregar a inicio</em>. Abriendo DeAutopartes desde ahí vas a poder
                            activar las notificaciones.
                        </p>
                    </>
                )}

                {estado === "bloqueado" && (
                    <>
                        <strong>Las notificaciones están bloqueadas</strong>
                        <p>
                            Se habilitan desde el candado de la barra de direcciones, en los permisos de
                            este sitio.
                        </p>
                    </>
                )}

                {estado === "error" && (
                    <>
                        <strong>No pudimos activarlas</strong>
                        <p>Probá de nuevo en un rato. Mientras tanto tenés todo en la campana del menú.</p>
                    </>
                )}

                {estado === "listo" && (
                    <>
                        <strong>¡Listo!</strong>
                        <p>Ya vas a recibir los avisos en este dispositivo.</p>
                    </>
                )}
            </div>

            <div className="pushOptIn__acciones">
                {estado === "oferta" && (
                    <button type="button" className="button" data-type="primary" onClick={activar}>
                        Activar
                    </button>
                )}
                {estado !== "listo" && (
                    <button type="button" className="pushOptIn__cerrar" onClick={posponer}
                            aria-label="Ahora no">
                        <FontAwesomeIcon icon="fa-solid fa-xmark"/>
                    </button>
                )}
            </div>
        </aside>
    );
};
